import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Checkbox,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from '../../../components/@extended/CustomButton';
import { CustomTableHead, CustomTableHeadCell, CustomTableFoot, CustomTableRow } from '../../../components/@extended/CustomTable';
import CustomTablePagination from '../../../components/common/TablePagination';
import { useGetSiteTransactionsQuery, useApproveTransactionsMutation, useRequestVerificationMutation } from '../../../store/slices/apiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { openMessage } from '../../../store/slices/showMessageSlice';
import { SyncProblemOutlined } from '@mui/icons-material';

const pendingColumns = [
  {
    id: 'select',
    label: '',
    minWidth: 50,
    align: 'center',
  },
  {
    id: 'recipientEmail',
    label: 'Recipient Email',
    minWidth: 170,
    align: 'left',
  },
  {
    id: 'event',
    label: 'Event',
    minWidth: 170,
    align: 'center',
  },
  {
    id: 'amount',
    label: 'Amount',
    minWidth: 170,
    align: 'center',
  },
  {
    id: 'status',
    label: 'Status',
    minWidth: 170,
    align: 'center',
  },
  {
    id: 'createdAt',
    label: 'Created Date',
    minWidth: 150,
    align: 'center',
  },
];

const PendingTransactionsDialog = ({ open, onClose, trialId, trialSiteId }) => {
  const dispatch = useDispatch();
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 5,
  });
  const actor = useSelector(state => state?.userDetails?.loggedInUser?.email)
  const [approveTransactions, { isLoading: isApproving }] = useApproveTransactionsMutation();
  const [requestVerification, { isLoading: verificationLoading }] = useRequestVerificationMutation();
  const { data: pendingData, refetch } = useGetSiteTransactionsQuery(
    {
      params: {
        page: pagination?.page + 1,
        limit: pagination?.rowsPerPage,
        clinicalStudyId: trialId,
        studySiteId: trialSiteId,
        transactionStatus: 'pending,failed',
        //   transactionStatus: 'pending',
      },
    },
    {
      skip: !open, // Only fetch when dialog is open
    }
  );

  const pendingRows = useMemo(() => {
    if (!pendingData?.transactions) return [];

    return pendingData.transactions.map(({ transaction: _trans }) => {
      const emails = _trans?.key?.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g);
      return {
        id: _trans?.id,
        recipientEmail: _trans?.recipientEntity?.email || emails?.[emails.length - 1] || '- -',
        recipientEntity: _trans?.recipientEntity,
        status: _trans?.transactionStatus,
        amount: (_trans?.amount / 100).toFixed(2),
        event: _trans?.event?.eventName,
        createdAt: new Date(_trans?.createdAt).toLocaleDateString(),
        totalAmount: ((_trans?.amount + _trans?.stripeTransactionFees + _trans?.tax) / 100).toFixed(2),
        stripeFees: (_trans?.stripeTransactionFees / 100).toFixed(2),
        tax: (_trans?.tax / 100).toFixed(2),
        applicationFees: (_trans?.applicationFees / 100).toFixed(2),
        paymentMetadata:
          typeof _trans?.paymentMetadata === 'string' ? JSON.parse(_trans?.paymentMetadata) : _trans?.paymentMetadata,
      };
    });
  }, [pendingData]);

  // Reset selected transactions when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedTransactions(new Set());
      setSelectAllPages(false);
      setPagination({ page: 0, rowsPerPage: 5 });
    }
  }, [open]);

  // Reset select all pages when data changes (pagination, etc.)
  useEffect(() => {
    if (selectAllPages && pendingData?.pagination?.total) {
      // If we had all pages selected, maintain that state with new data
      const totalTransactionIds = new Set();
      // Note: We'll need all transaction IDs, but for now we work with available data
      pendingRows.forEach((row) => totalTransactionIds.add(row.id));
      setSelectedTransactions(totalTransactionIds);
    }
  }, [pendingData, pendingRows, selectAllPages]);

  const handleSelectTransaction = (transactionId) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = new Set(pendingRows.map((row) => row.id));
      setSelectedTransactions(newSelected);
    } else {
      setSelectedTransactions(new Set());
    }
  };

  const handleApproveSelected = async () => {
    if (selectedTransactions.size === 0) {
      dispatch(
        openMessage({
          messageSeverity: 'warning',
          message: 'Please select at least one transaction to approve.',
        })
      );
      return;
    }

    try {
      const transactionIds = Array.from(selectedTransactions);

      if (selectAllPages) {
        const payload = {
          approveAll: true,
          clinicalStudyId: trialId,
          studySiteId: trialSiteId,
        };

        await approveTransactions({payload, actor}).unwrap();

        dispatch(
          openMessage({
            messageSeverity: 'success',
            message: `All ${
              pendingData?.summary?.statusCounts?.pending || 0
            } pending transactions approved successfully!`,
          })
        );
      } else {
        // Option 2: Send specific transaction IDs
        const payload = {
          transactionIds,
          clinicalStudyId: trialId,
          studySiteId: trialSiteId,
        };
        const result = await approveTransactions({payload, actor}).unwrap();

        dispatch(
          openMessage({
            messageSeverity: result.failedCount > 0 ? 'warning' : 'success',
            message: result.message || `${transactionIds.length} transaction(s) approved successfully!`,
          })
        );
      }

      setSelectedTransactions(new Set());
      setSelectAllPages(false);
      refetch(); // Refresh the pending transactions
      onClose(); // Close the dialog
    } catch (error) {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: 'Failed to approve transactions. Please try again.',
        })
      );
    }
  };

  const handleRequestVerification = async (entity) => {
    try {
      const res = await requestVerification(entity?.id).unwrap();
      if (res?.success) {
        dispatch(
          openMessage({
            messageSeverity: 'success',
            message: `Requested verification for ${entity?.email}`,
          })
        );
      }
    } catch (error) {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: `Failed to request verification for ${entity?.email}`,
        })
      );
    }
  };

  const onNewPage = (page, rowsPerPage) => {
    setPagination({ page, rowsPerPage });
    // Don't clear selection when changing pages to maintain cross-page selection
    // Only clear if not in "select all pages" mode
    if (!selectAllPages) {
      // Optionally clear selection on page change, or maintain it
      // setSelectedTransactions(new Set());
    }
  };

  // Calculate selection states
  const currentPageIds = pendingRows.map((row) => row.id);
  const currentPageSelectedCount = currentPageIds.filter((id) => selectedTransactions.has(id)).length;
  const isCurrentPageFullySelected = pendingRows.length > 0 && currentPageSelectedCount === pendingRows.length;
  const isCurrentPagePartiallySelected = currentPageSelectedCount > 0 && currentPageSelectedCount < pendingRows.length;

  // Determine checkbox states
  const isAllSelected = selectAllPages || isCurrentPageFullySelected;
  const isIndeterminate =
    !selectAllPages &&
    (isCurrentPagePartiallySelected || (selectedTransactions.size > 0 && !isCurrentPageFullySelected));

  // Calculate display counts
  const getSelectedCount = () => {
    if (selectAllPages) {
      return pendingData?.summary?.statusCounts?.pending || pendingData?.pagination?.total || 0;
    }
    return selectedTransactions.size;
  };

  const getSelectedDisplayText = () => {
    const selectedCount = getSelectedCount();
    if (selectAllPages) {
      return `All ${selectedCount} transactions selected (across all pages)`;
    }
    return `${selectedCount} transaction(s) selected`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">
          Pending Transactions Approval
          {pendingData?.pagination?.total > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({pendingData.pagination.total} total)
            </Typography>
          )}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader aria-label="pending transactions table">
            <CustomTableHead>
              <TableRow>
                {pendingColumns.map((column) => {
                  if (column.id === 'select') {
                    return (
                      <CustomTableHeadCell key={column.id} align={column.align} sx={{ width: column.minWidth }}>
                        <Checkbox
                          indeterminate={isIndeterminate}
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          disabled={pendingRows.length === 0}
                          size="small"
                        />
                      </CustomTableHeadCell>
                    );
                  }
                  return (
                    <CustomTableHeadCell key={column.id} align={column.align} sx={{ minWidth: column.minWidth }}>
                      {column.label}
                    </CustomTableHeadCell>
                  );
                })}
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {pendingRows.map((row, index) => {
                const isSelected = selectedTransactions.has(row.id);
                return (
                  <TableRow
                    hover
                    key={row.id}
                    selected={isSelected}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSelectTransaction(row.id)}
                  >
                    {pendingColumns.map((column) => {
                      if (column.id === 'select') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectTransaction(row.id)}
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                        );
                      }
                      {
                        if (column.id === 'recipientEmail') {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <Typography variant="subtitle2" color="initial">
                                {row[column.id]}
                                {row?.recipientEntity?.accountStatus === 'Pending' && (
                                  <Tooltip
                                    title={
                                      <Box sx={{ p: 1 }}>
                                        <Typography variant="body2">
                                          Onboarding/KYC pending: Request verification
                                        </Typography>
                                      </Box>
                                    }
                                    arrow
                                  >
                                    <IconButton
                                      aria-label="close"
                                      onClick={() => handleRequestVerification(row?.recipientEntity)}
                                      disabled={verificationLoading}
                                      sx={{
                                        color: (theme) => theme.palette.warning.main,
                                      }}
                                    >
                                      <SyncProblemOutlined />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Typography>
                            </TableCell>
                          );
                        }
                      }
                      if (column.id === 'amount') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Tooltip
                              title={
                                <Box sx={{ p: 1 }}>
                                  <Typography variant="body2">Tax: ${row.tax}</Typography>
                                  <Typography variant="body2">Stripe Fees: ${row.stripeFees}</Typography>
                                  <Typography variant="body2">
                                    Application fees: ${row.applicationFees === 0 ? 0.1 : 0.1}
                                  </Typography>
                                  <Typography variant="body2">Total: ${row.totalAmount}</Typography>
                                </Box>
                              }
                              arrow
                            >
                              <Typography variant="subtitle2" color="primary">
                                ${row[column.id]}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        );
                      }
                      if (column.id === 'status') {
                        return (
                          <TableCell key={index + column.id} align={column.align}>
                            {row.paymentMetadata?.reason ? (
                              <Tooltip
                                title={
                                  <Box sx={{ p: 1 }}>
                                    <Typography variant="body2">{row.paymentMetadata?.reason}</Typography>
                                  </Box>
                                }
                                arrow
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    color:
                                      row[column.id] === 'succeeded'
                                        ? 'green'
                                        : row[column.id] === 'failed'
                                        ? 'red'
                                        : 'orange',
                                  }}
                                  fontWeight={600}
                                >
                                  {row[column.id]}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color:
                                    row[column.id] === 'succeeded'
                                      ? 'green'
                                      : row[column.id] === 'FAILED'
                                      ? 'red'
                                      : 'orange',
                                }}
                                fontWeight={600}
                              >
                                {row[column.id]}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={column.id} align={column.align}>
                          <Typography variant="subtitle2" color="initial">
                            {row[column.id]}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {pendingRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={pendingColumns.length} align="center">
                    <Typography variant="subtitle2" color="text.secondary" sx={{ py: 4 }}>
                      No pending transactions found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {pendingData?.pagination && (
              <CustomTableFoot>
                <CustomTableRow>
                  <CustomTableHeadCell colSpan={pendingColumns.length}>
                    <CustomTablePagination
                      page={pagination?.page}
                      rowsPerPage={pagination?.rowsPerPage}
                      count={pendingData?.pagination?.total}
                      onNewPage={onNewPage}
                    />
                  </CustomTableHeadCell>
                </CustomTableRow>
              </CustomTableFoot>
            )}
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          {selectedTransactions.size > 0 && (
            <Typography variant="body2" color="primary">
              {selectedTransactions.size} transaction(s) selected
            </Typography>
          )}
        </Box>
        <CustomButton variant="outlined" onClick={onClose}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={handleApproveSelected}
          disabled={selectedTransactions.size === 0 || isApproving}
          loading={isApproving}
        >
          Approve Selected ({selectedTransactions.size})
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default PendingTransactionsDialog;